// Convert a dropped/pasted image file into an inline JPEG data URL suitable
// for the Sogni vision chat API (base64 JPEG/PNG only, longest side <= 1024px).

const MAX_DIMENSION = 1024;
const JPEG_QUALITIES = [0.85, 0.7, 0.55, 0.4];
// Stay safely below the server's 1,000,000-char data-URL cap (plus JSON overhead)
const MAX_DATA_URL_CHARS = 900000;
export const MAX_IMAGE_FILE_BYTES = 20 * 1024 * 1024;

const HEIC_MIME_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence'
]);
const IMAGE_EXTENSION_PATTERN = /\.(avif|bmp|gif|heic|heif|jpe?g|png|webp)$/i;
const HEIC_EXTENSION_PATTERN = /\.(heic|heif)$/i;

function isHeicFile(file) {
  if (!file) return false;
  const type = String(file.type || '').toLowerCase();
  return HEIC_MIME_TYPES.has(type) || HEIC_EXTENSION_PATTERN.test(file.name || '');
}

function isImageFile(file) {
  if (!file) return false;
  const type = String(file.type || '').toLowerCase();
  return type.startsWith('image/') || IMAGE_EXTENSION_PATTERN.test(file.name || '');
}

function formatHeicError(error) {
  if (!error) return 'unknown error';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;

  const code = error?.code?.value ?? error?.code;
  const subcode = error?.subcode?.value ?? error?.subcode;
  if (code !== undefined || subcode !== undefined) {
    return `libheif code ${code ?? 'unknown'}, subcode ${subcode ?? 'unknown'}`;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

async function loadImageFromObjectUrl(objectUrl) {
  const img = new Image();
  if (typeof img.decode === 'function') {
    img.src = objectUrl;
    await img.decode();
    return img;
  }

  return new Promise((resolve, reject) => {
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Couldn't read that image file"));
    img.src = objectUrl;
  });
}

// Pull the first image file out of a DataTransfer (drop) or clipboardData (paste).
export function extractImageFile(dataTransfer) {
  if (!dataTransfer) return null;

  for (const item of dataTransfer.items || []) {
    if (item.kind === 'file') {
      const file = item.getAsFile();
      if (isImageFile(file)) return file;
    }
  }

  for (const file of dataTransfer.files || []) {
    if (isImageFile(file)) return file;
  }

  return null;
}

function encodeCanvasToJpegDataUrl(canvas) {
  for (const quality of JPEG_QUALITIES) {
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (dataUrl.length <= MAX_DATA_URL_CHARS) return dataUrl;
  }

  throw new Error('That image is too detailed to process — try a smaller or simpler image');
}

async function decodeHeicFile(file) {
  const heicDecodeModule = await import('heic-decode');
  const decodeHeic = heicDecodeModule.default || heicDecodeModule;
  if (typeof decodeHeic !== 'function') {
    throw new Error('HEIC decoder did not load');
  }

  const decoded = await decodeHeic({ buffer: new Uint8Array(await file.arrayBuffer()) });
  if (!decoded?.width || !decoded?.height || !decoded?.data) {
    throw new Error('HEIC decoder returned an invalid image');
  }

  return decoded;
}

async function heicFileToJpegDataUrl(file) {
  const decoded = await decodeHeicFile(file);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(decoded.width, decoded.height));
  const width = Math.max(1, Math.round(decoded.width * scale));
  const height = Math.max(1, Math.round(decoded.height * scale));

  const sourceCanvas = document.createElement('canvas');
  sourceCanvas.width = decoded.width;
  sourceCanvas.height = decoded.height;
  const sourceCtx = sourceCanvas.getContext('2d');
  sourceCtx.putImageData(new ImageData(decoded.data, decoded.width, decoded.height), 0, 0);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(sourceCanvas, 0, 0, width, height);

  return encodeCanvasToJpegDataUrl(canvas);
}

export async function imageFileToJpegDataUrl(file) {
  if (file.size > MAX_IMAGE_FILE_BYTES) {
    throw new Error('That image is too large (max 20MB)');
  }

  try {
    return await readableImageToJpegDataUrl(file);
  } catch (nativeError) {
    if (!isHeicFile(file)) {
      if (nativeError?.name === 'EncodingError') {
        throw new Error("Couldn't read that image file");
      }
      throw nativeError;
    }

    console.warn(`Native HEIC decode failed: ${formatHeicError(nativeError)}. Trying HEIC converter.`);
  }

  try {
    return await heicFileToJpegDataUrl(file);
  } catch (converterError) {
    console.warn(`HEIC conversion failed: ${formatHeicError(converterError)}`, converterError);
    throw new Error("Couldn't read that HEIC image file. Try converting it to JPEG first.");
  }
}

async function readableImageToJpegDataUrl(imageBlob) {
  const objectUrl = URL.createObjectURL(imageBlob);
  try {
    const img = await loadImageFromObjectUrl(objectUrl);

    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
    const width = Math.max(1, Math.round(img.naturalWidth * scale));
    const height = Math.max(1, Math.round(img.naturalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    // White backing so transparent PNGs don't turn black when re-encoded as JPEG
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    return encodeCanvasToJpegDataUrl(canvas);
  } catch (error) {
    if (error?.name === 'EncodingError') {
      throw new Error("Couldn't read that image file");
    }
    throw error;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
